import React, {useEffect, useState, useCallback, useRef} from "react";
import {
    BrowserRouter as Router, Routes, Route,
    useLocation, useNavigate,
} from "react-router-dom";

import SearchBarContainer from "./search/SearchBarContainer";
import PropertyCard from "./components/PropertyCard";
import FloatingWhatsapp from "./components/FloatingWhatsapp";
import Footer from "./components/Footer";
import { AuthenticationPage, authenticationApi, sessionStorageService, AUTH_STORAGE_KEYS } from "./features/authentication";
import { FavoritesProvider } from "./features/favorites/FavoritesContext";
import useClickOutside from "./search/hooks/useClickOutside";
import Icon from "./components/ai/Icon";
import SettingsModal from "./components/ai/SettingsModal";
import Contact from "./pages/Contact";
import PropertyDetails from "./components/propertydetails/PropertyDetails";
import About from "./pages/About";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AiSearchPage from "./components/ai/AiSearchPage";
import AgentAdminApp from "./components/admindashboard/Agentadminapp";
import BookTourPage from "./components/admindashboard/BookTourPage";
import BrokerRegistration, {BrokerLogin, BrokerDashboard} from "./components/broker";
import LiveJoinPage from "./components/propertydetails/LiveJoinPage";
import LiveRoomPage from "./components/propertydetails/LiveRoomPage";

import "./styles/App.css";

const BASE_URL           = process.env.REACT_APP_API_BASE;
const HIDE_FOOTER_ROUTES = new Set(["/ai-search", "/dashboard", "/admin", "/agent-admin", "/book"]);

function MetaPixelTracker() {
    const location = useLocation();
    useEffect(() => {
        window.fbq?.("track", "PageView");
    }, [location.pathname]);
    return null;
}

function App() {
    return <Router><AppContent/></Router>;
}

function AppContent() {
    const location = useLocation();
    const navigate = useNavigate();

    const [properties, setProperties] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(
        () => Boolean(sessionStorageService.get())
    );
    const [currentUser, setCurrentUser] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [theme, setTheme] = useState(
        () => localStorage.getItem("ogm_theme") || "light"
    );

    // The JWT only encodes email, so the verified name/avatar for the
    // header dropdown has to come from /api/auth/me, not the token.
    useEffect(() => {
        if (!isAuthenticated) {
            setCurrentUser(null);
            return;
        }
        authenticationApi.getCurrentUser()
            .then((data) => setCurrentUser(data))
            .catch(() => setCurrentUser(null));
    }, [isAuthenticated]);

    // Keeps this tab's auth state in sync when login/logout happens in
    // ANOTHER same-origin tab (e.g. an email confirmation link that opened
    // in a new tab) - the `storage` event fires here automatically since
    // it never fires in the tab that made the change itself.
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === AUTH_STORAGE_KEYS.TOKEN) {
                setIsAuthenticated(Boolean(e.newValue));
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        localStorage.setItem("ogm_theme", theme);
    }, [theme]);

    const isAgentAdmin = location.pathname.startsWith("/agent-admin");
    const isLiveJoin = location.pathname.startsWith("/live/join");
    const isLiveRoom = location.pathname.startsWith("/live/") &&
        !location.pathname.startsWith("/live/join");
    const isAuthPage = location.pathname === "/auth";
    const shouldHideFooter = isAuthPage || HIDE_FOOTER_ROUTES.has(location.pathname);

    const loadProperties = useCallback(async (query = "") => {
        try {
            const params = new URLSearchParams({page: 0, size: 50});
            if (query) params.set("q", query);
            const res = await fetch(`${BASE_URL}/api/properties?${params}`);
            const data = await res.json();
            setProperties(data?.content ?? []);
        } catch (err) {
            console.error("Failed to fetch:", err);
            setProperties([]);
        }
    }, []);

    useEffect(() => {
        if (!isAgentAdmin && !isLiveJoin && !isLiveRoom) loadProperties();
    }, [loadProperties, isAgentAdmin, isLiveJoin, isLiveRoom]);

    const handleSearch = useCallback((data) => {
        if (data === null) {
            setIsSearching(false);
            loadProperties();
            return;
        }
        setIsSearching(true);
        loadProperties(data);
    }, [loadProperties]);

    if (isAgentAdmin) return <><MetaPixelTracker/><AgentAdminApp/></>;

    if (isLiveJoin) return (
        <><MetaPixelTracker/>
            <Routes>
                <Route path="/live/join/:requestId" element={<LiveJoinPage/>}/>
            </Routes>
        </>
    );

    if (isLiveRoom) return (
        <><MetaPixelTracker/>
            <Routes>
                <Route path="/live/:roomName" element={<LiveRoomPage/>}/>
            </Routes>
        </>
    );

    return (
        <>
            <MetaPixelTracker/>
            <FavoritesProvider isAuthenticated={isAuthenticated}>
                <div className="app-container">
                    {!isAuthPage && (
                        <Header
                            isAuthenticated={isAuthenticated}
                            currentUser={currentUser}
                            onLogout={() => {
                                sessionStorageService.remove();
                                setIsAuthenticated(false);
                                setCurrentUser(null);
                                navigate("/");
                            }}
                            onLogoClick={() => {
                                handleSearch(null);
                                navigate("/", {replace: true});
                            }}
                            onOpenSettings={() => setSettingsOpen(true)}
                        />
                    )}
                    <Routes>
                        <Route path="/"
                               element={<HomePage properties={properties} isSearching={isSearching}
                                                  onSearch={handleSearch}/>}
                        />
                        <Route path="/property/:slug" element={<PropertyDetails/>}/>
                        <Route path="/book/:slug" element={<BookTourPage/>}/>
                        <Route path="/about" element={<About/>}/>
                        <Route path="/privacy-policy" element={<PrivacyPolicy/>}/>
                        <Route path="/contact" element={<Contact/>}/>
                        <Route path="/ai-search" element={<AiSearchPage/>}/>
                        <Route path="/broker/register" element={<BrokerRegistration/>}/>
                        <Route path="/broker/login" element={<BrokerLogin/>}/>
                        <Route path="/broker/dashboard" element={<BrokerDashboard/>}/>
                        <Route path="/auth"
                               element={<AuthenticationPage onAuthSuccess={() => setIsAuthenticated(true)}/>}
                        />
                    </Routes>
                    {!isAuthPage && <FloatingWhatsapp/>}
                    {!shouldHideFooter && <Footer/>}
                    {isAuthenticated && (
                        <SettingsModal
                            open={settingsOpen}
                            onClose={() => setSettingsOpen(false)}
                            user={{name: currentUser?.fullName, email: currentUser?.email}}
                            theme={theme}
                            onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
                        />
                    )}
                </div>
            </FavoritesProvider>
        </>
    );
}

function Header({isAuthenticated, currentUser, onLogout, onLogoClick, onOpenSettings}) {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useClickOutside(menuRef, () => setMenuOpen(false), menuOpen);

    const name = currentUser?.fullName || "Account";
    const email = currentUser?.email || "";
    const initials = name.charAt(0).toUpperCase();

    return (
        <header className="topbar">
            <div
                className="header-left"
                onClick={onLogoClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && onLogoClick()}
                aria-label="Go to homepage"
            >
                <img src="/logo.png" alt="OGM" className="logo-img"/>
                <span className="header-sub">One Global Marketplace</span>
            </div>
            <div className="header-actions">
                {isAuthenticated ? (
                    <div className="header-profile" ref={menuRef}>
                        <button
                            type="button"
                            className="header-profile-trigger"
                            onClick={() => setMenuOpen((o) => !o)}
                            aria-haspopup="true"
                            aria-expanded={menuOpen}
                            aria-label={`Account menu for ${name}`}
                        >
                            <span className="header-avatar">{initials}</span>
                        </button>

                        {menuOpen && (
                            <div className="header-profile-menu" role="menu">
                                <div className="header-profile-menu-info">
                                    <div className="name">{name}</div>
                                    {email && <div className="email">{email}</div>}
                                </div>
                                <div className="header-profile-menu-divider"/>
                                <button
                                    type="button"
                                    className="header-profile-menu-item"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onOpenSettings();
                                    }}
                                >
                                    <Icon name="settings" size={16}/>
                                    Account Settings
                                </button>
                                <div className="header-profile-menu-divider"/>
                                <button
                                    type="button"
                                    className="header-profile-menu-item danger"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onLogout();
                                    }}
                                >
                                    <Icon name="logout" size={16}/>
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <button className="contact" onClick={() => navigate("/auth")}>Login</button>
                )}
            </div>
        </header>
    );
}

function HomePage({properties, isSearching, onSearch}) {
    return (
        <main className="main-section">
            <SearchBarContainer onSearch={onSearch}/>
            <h2 className="section-title">
        <span className="section-title-text">
          {isSearching ? "Search Results" : "Popular Homes in Bengaluru"}
        </span>
            </h2>
            <PropertyList properties={properties}/>
        </main>
    );
}

function PropertyList({properties}) {
    if (!properties?.length) {
        return (
            <div className="no-results-container">
                <div className="no-results-content">
                    <span className="no-results-icon">🏠</span>
                    <h3>No properties found</h3>
                    <p>We couldn't find any listings matching your criteria.</p>
                    <button className="reset-search-btn" onClick={() => window.location.reload()}>
                        Clear all filters
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className="property-grid">
            {properties.map((prop) => <PropertyCard key={prop.id} property={prop}/>)}
        </div>
    );
}

export default App;